/**
 * MAILBOX-UNIT dashboard (D-247, 2026-10-06 operator Q17/Q46) — ONE shared block
 * rendered by every "Sağlayıcılar · Mailbox" page: summary cards (total,
 * sellable, delivered, REAL refunds), per-site sellable stock, and the
 * "delivered order without a burned key" invariant. The per-mailbox
 * site history lives in the shared <MailboxUnitManager> (lazy, per row).
 *
 * The rule it explains (the server enforces it; mail.com and the old GMX alias
 * line are exempt):
 *   * mail arrived  -> the (mailbox, site) pair is closed for everyone;
 *   * no mail (süre bitti / müşteri iptal) -> the mailbox is sellable again at
 *     once to OTHER customers, never again to the same customer for that site;
 *   * a different site is a different right; applies from now on only.
 *
 * Presentation only: every number comes from
 * GET /admin/providers/:id/parents/site-dashboard. No customer identity is
 * shown anywhere (Q17/Q52).
 */
import { useApiQuery } from '../../api/hooks.js';
import { Badge } from '../../ui/index.js';
import {
  fetchMailboxDashboard,
  isMailboxDashboard,
  percentOf,
  type MailboxDashboard,
  type MailboxSiteStockRow,
} from './mailboxSaleRuleApi.js';
import type { MailboxUnitProviderId } from './mailboxUnitApi.js';

import '../../styles/mailboxunit.mobile.css';

export interface MailboxUnitDashboardProps {
  readonly providerId: MailboxUnitProviderId;
  /** Operator-facing provider name, e.g. "rambler.ru". */
  readonly providerLabel: string;
}

/** The rule, in the words the operator used (pinned by site-rule-copy.test.ts). */
export const SALE_RULE_TEXT =
  'Posta gelince o (mailbox, site) çifti herkese kalıcı kapanır. Posta gelmezse (süre bitti ya da müşteri iptal etti) mailbox hemen başka müşterilere satılabilir; aynı müşteriye aynı site için bir daha satılmaz. Başka bir site ayrı bir haktır. Kural yalnız bundan sonrası için geçerlidir: eski kuralla satış anında yanmış çiftler kapalı kalır.';

function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString('tr-TR') : '—';
}

/** Bar rengi: kapanan pay büyüdükçe dikkat çeksin (yeşil → sarı → kırmızı). */
function shareClass(sharePct: number): string {
  if (sharePct >= 50) return ' is-high';
  if (sharePct >= 20) return ' is-mid';
  return '';
}

export function MailboxUnitDashboard({ providerId, providerLabel }: MailboxUnitDashboardProps): JSX.Element {
  const query = useApiQuery<MailboxDashboard>((o) => fetchMailboxDashboard(o, providerId), [providerId]);
  const testPrefix = providerId;

  if (query.kind === 'idle' || query.kind === 'loading') {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" data-testid={`${testPrefix}-dashboard-loading`}>Satış kuralı özeti yükleniyor…</div>
      </div>
    );
  }

  if (query.kind !== 'ok' || !isMailboxDashboard(query.data)) {
    const text =
      query.kind === 'error'
        ? query.reason
        : query.kind === 'auth_required'
          ? 'Oturum gerekli.'
          : 'Beklenmeyen yanıt.';
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" role="alert" data-testid={`${testPrefix}-dashboard-error`}>
          <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
          {providerLabel} satış kuralı özeti alınamadı: {text}
        </div>
      </div>
    );
  }

  const { cards, sites, invariants } = query.data;
  const closedByOldRule = Math.max(0, cards.closedPairs - cards.deliveredPairs);
  const invariant = invariants?.deliveredWithoutKey;

  return (
    <>
      <p className="t-small t-tertiary" data-testid={`${testPrefix}-sale-rule`} style={{ margin: '0 0 12px' }}>
        {SALE_RULE_TEXT}
      </p>

      {invariant && invariant.count !== 0 ? (
        <div
          className="card"
          role="alert"
          data-testid={`${testPrefix}-invariant-alert`}
          style={{ marginBottom: 16 }}
        >
          <div className="card-body">
            {invariant.count === null ? (
              <>
                <Badge tone="warn">Kontrol edilemedi</Badge>{' '}
                Posta gelen siparişlerin çiftinin kapanıp kapanmadığı kontrolü şu an çalışmadı; sonuç bilinmiyor.
              </>
            ) : (
              <>
                <Badge tone="danger">Tutarsızlık</Badge>{' '}
                Son {Math.round(invariant.lookBackMinutes / 60)} saatte <strong>{invariant.count}</strong> siparişe posta
                geldi ama (mailbox, site) çifti kapanmadı — bu mailbox aynı site için başka müşteriye yeniden
                satılabilir. Çalışma zamanındaki yanma listesini (SITE_KEY_BURN_ON_ARRIVAL_PROVIDERS) ve mail okuma
                servisini kontrol edin.{' '}
                {invariant.orders.slice(0, 5).map((o) => (
                  <span key={o.orderId} className="t-mono" style={{ marginRight: 8 }}>
                    {o.site} · {o.orderId}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="stat" data-testid="kpi-total">
          <div className="stat-label">Toplam stok</div>
          <div className="stat-value">{cards.total}</div>
          <div className="stat-meta">mailbox · {cards.inUse} şu an müşteride</div>
        </div>
        <div className="stat" data-testid="kpi-sellable">
          <div className="stat-label">Satılabilir</div>
          <div className="stat-value">{cards.sellable}</div>
          <div className="stat-meta up">stokun %{percentOf(cards.sellable, cards.total)} · müşteriye gösterilen stok</div>
        </div>
        <div className="stat" data-testid="kpi-delivered">
          <div className="stat-label">Posta gelen</div>
          <div className="stat-value">{cards.deliveredPairs}</div>
          <div className="stat-meta">
            (mailbox, site) çifti · {cards.deliveredMailboxes} mailbox · herkese kapalı
            {closedByOldRule > 0 ? ` · +${closedByOldRule} eski kuralla kapalı` : ''}
          </div>
        </div>
        <div className="stat" data-testid="kpi-refunds">
          <div className="stat-label">Gerçek iade</div>
          <div className="stat-value">{cards.refundedOrders}</div>
          <div className="stat-meta">
            iade kaydı olan sipariş · posta gelmeyen {cards.noMailServes} satış (yeniden satılabilir)
          </div>
        </div>
      </div>

      {cards.legacyServes > 0 ? (
        <p className="t-small t-tertiary" data-testid={`${testPrefix}-legacy-note`} style={{ margin: '0 0 12px' }}>
          {cards.legacyServes} satış kayıt defterinden (migration 0085) önce yapıldı; bunların sonucu bilinmiyor ve
          sipariş bağlantısı yok. Yukarıdaki iade / posta sayıları yalnız defterdeki {cards.ledgerServes} satışı ve
          kalıcı anahtarları kapsar.
        </p>
      ) : null}

      <div className="card mbu-site-card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Site başına satılabilir stok</div>
            <div className="card-subtitle">
              Her site için şu an kaç mailbox satılabilir; posta gelen çift o site için herkese kapalıdır, posta gelmeyen
              satış mailbox'ı yeniden satışa açar (yalnız o müşteriye kapalı kalır).
            </div>
          </div>
          <div className="card-header-actions">
            <span className="badge badge-neutral">{sites.length} site</span>
          </div>
        </div>
        {sites.length === 0 ? (
          <div className="card-body" data-testid={`${testPrefix}-no-sites`}>
            <span className="badge badge-success" style={{ marginRight: 8 }}>Temiz</span>
            Henüz hiçbir site için satış yok — tüm stok her site için kullanılabilir.
          </div>
        ) : (
          <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
            <table
              className="tbl tbl-stack tbl-stack-xl mbu-site-table"
              data-testid={`${testPrefix}-site-table`}
            >
              <thead>
                <tr>
                  <th>Site</th>
                  <th title="Bu site için şu an herhangi bir müşteriye verilebilecek mailbox sayısı">Satılabilir stok</th>
                  <th title="Posta gelen çift: o site için herkese kalıcı kapalı">Posta gelen (kapalı)</th>
                  <th title="Posta gelmeden biten satış: mailbox yeniden satılabilir, yalnız o müşteriye kapalı">Posta gelmeyen (yeniden satılabilir)</th>
                  <th style={{ minWidth: 200 }}>Kapalı payı</th>
                  <th>Son satış</th>
                  <th>Son kapanma</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((row) => (
                  <SiteRow key={row.site} row={row} total={cards.total} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SiteRow({ row, total }: { readonly row: MailboxSiteStockRow; readonly total: number }): JSX.Element {
  const share = percentOf(row.closedMailboxes, total);
  return (
    <tr data-testid={'site-row-' + row.site}>
      <td className="t-mono tbl-stack-primary mbu-site-td" data-label="Site">{row.site}</td>
      <td className="mbu-num-td" data-label="Satılabilir stok" data-testid={'site-sellable-' + row.site}>
        {row.sellableMailboxes}
      </td>
      <td className="mbu-num-td" data-label="Posta gelen (kapalı)" data-testid={'site-delivered-' + row.site}>
        {row.deliveredMailboxes}
        {row.closedByOldRuleMailboxes > 0 ? (
          <span
            className="t-xs t-tertiary"
            data-testid={'site-old-rule-' + row.site}
            title="Eski kuralla satış anında yanmış çift (posta gelmedi); Q23: yalnız bundan sonrası için, eski çiftler kapalı kalır"
          >
            {' '}+{row.closedByOldRuleMailboxes} eski kural
          </span>
        ) : null}
      </td>
      <td className="mbu-num-td" data-label="Posta gelmeyen (yeniden satılabilir)" data-testid={'site-nomail-' + row.site}>
        {row.noMailMailboxes}
      </td>
      <td className="rambler-share-td tbl-stack-status mbu-share-td" data-label="Kapalı payı">
        <div className="rambler-share mbu-share">
          <span className="rambler-bar" aria-hidden="true">
            <span
              className={'rambler-bar-fill' + shareClass(share)}
              style={{ width: Math.min(100, share) + '%' }}
            />
          </span>
          <span className="badge badge-neutral" data-testid={'site-share-' + row.site}>
            %{share}
          </span>
        </div>
      </td>
      <td className="t-small t-tertiary mbu-date-td" data-label="Son satış">{fmt(row.lastServedAt)}</td>
      <td className="t-small t-tertiary mbu-date-td" data-label="Son kapanma">{fmt(row.lastClosedAt)}</td>
    </tr>
  );
}
